import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import cn from 'classnames';
import creditCardType, { types as CardType, getTypeInfo } from 'credit-card-type';

import { evAddAddressClick } from 'events/account';

import {
  formatCreditCard,
  getPaymentType,
  isExpirationDateValid,
  normalizePaymentType
} from 'helpers/MyAccountUtils';
import { makeFormErrorMessage } from 'helpers/MyAccountRenderUtils';
import { isValidCreditCardNumber } from 'helpers/PaymentUtils';
import { ChangeDisclaimer } from 'components/account/Disclaimers';
import MartyLink from 'components/common/MartyLink';
import PaymentFormAddressCard from 'components/account/PaymentFormAddressCard';
import { titaniteView, track } from 'apis/amethyst';
import css from 'styles/components/account/paymentForm.scss';
import formStyles from 'styles/components/account/forms.scss';

const PAYMENT_VALIDATION_ERRORS = {
  'addCreditCardNumber': 'Please enter a valid card number.',
  'expirationDate': 'Please enter a valid expiration date.'
};

class PaymentForm extends Component {
  state = {
    errors: [],
    isPrimary: this.props.payment.primaryPaymentMethod
  };

  componentDidMount() {
    titaniteView();
  }

  handleFullNameChange = event => {
    const { errors } = this.state;
    const { onUpdateFullName } = this.props;
    onUpdateFullName(event.currentTarget.value);
    this.setState({ errors: errors.filter(error => error !== 'fullName') });
  };

  handlePrimaryPaymentMethodChange = () => {
    this.setState({ isPrimary: !this.state.isPrimary });
  };

  handleCardNumberChange = event => {
    const { errors } = this.state;
    const { onUpdateCardNumber } = this.props;
    const { value } = event.target;
    onUpdateCardNumber(value);
    this.setState({ errors: errors.filter(error => error !== 'addCreditCardNumber') });
  };

  handleAddressChange = event => {
    const { onUpdateBillingAddress } = this.props;
    const { value } = event.target;
    onUpdateBillingAddress(value);
  };

  handleExpirationMonthChange = event => {
    const { errors } = this.state;
    const { onUpdateExpirationMonth } = this.props;
    const { value } = event.target;
    onUpdateExpirationMonth(value);
    this.setState({ errors: errors.filter(error => error !== 'expirationDate') });
  };

  handleExpirationYearChange = event => {
    const { errors } = this.state;
    const { onUpdateExpirationYear } = this.props;
    const { value } = event.target;
    onUpdateExpirationYear(value);
    this.setState({ errors: errors.filter(error => error !== 'expirationDate') });
  };

  handleCancelClick = () => {
    const { returnTo } = this.props;
    this.context.router.pushPreserveAppRoot(returnTo || '/payments');
  };

  handleAddNewAddressClick = () => {
    track(evAddAddressClick, { addressType: 1 }); // amethyst addressType 1 => billing
  };

  handleSubmit = (event, win = window) => {
    event.preventDefault();
    const { isPrimary } = this.state;
    const { payment, onUpdatePaymentInfo, returnTo } = this.props;
    const {
      addressId,
      addCreditCardNumber,
      expirationMonth,
      expirationYear,
      fullName,
      paymentInstrumentId,
      paymentTime
    } = payment;
    // Initialize card object
    const card = { isPrimary };
    const errors = [];
    // Card number and payment type
    if (paymentInstrumentId) {
      card.paymentInstrumentId = paymentInstrumentId;
    } else {
      card.cc = formatCreditCard(addCreditCardNumber);
      card.paymentType = paymentTime || getPaymentType(card.cc);
    }
    card.name = fullName;
    card.expiration = `${expirationMonth}/${expirationYear}`;
    // Check if credit card is valid when there is no paymentInstrumentId (adding new payment)
    if (!paymentInstrumentId && !isValidCreditCardNumber(card.cc)) {
      errors.push('addCreditCardNumber');
    }
    // Name cannot be blank or just contain whitespace
    if (!fullName.trim()) {
      errors.push('fullName');
    }
    // Address must be selected
    if (!addressId) {
      errors.push('billingAddress');
    }
    // Expiration date must be valid
    if (!isExpirationDateValid(expirationMonth, expirationYear)) {
      errors.push('expirationDate');
    }
    if (errors.length > 0) {
      this.setState({ errors });
      // Scroll to top
      win.scrollTo(0, 0);
    } else {
      onUpdatePaymentInfo(card, addressId, returnTo);
    }
  };

  makeCreditCardIcons = () => {
    const { payment: { paymentType, addCreditCardNumber } } = this.props;
    const existingPaymentType = normalizePaymentType(paymentType);
    const ccType = existingPaymentType ? [{ type: existingPaymentType }] : creditCardType(addCreditCardNumber);

    const isVisa = ccType.length === 1 && ccType.find(card => card.type === CardType.VISA);
    const isMasterCard = ccType.length === 1 && ccType.find(card => card.type === CardType.MASTERCARD);
    const isDiscover = ccType.length === 1 && ccType.find(card => card.type === CardType.DISCOVER);
    const isAmex = ccType.length === 1 && ccType.find(card => card.type === CardType.AMERICAN_EXPRESS);

    return [
      <span key={CardType.VISA} className={cn(css.visa, { [css.active]: isVisa })}/>,
      <span key={CardType.MASTERCARD} className={cn(css.masterCard, { [css.active]: isMasterCard })}/>,
      <span key={CardType.DISCOVER} className={cn(css.discover, { [css.active]: isDiscover })}/>,
      <span key={CardType.AMERICAN_EXPRESS} className={cn(css.amex, { [css.active]: isAmex })}/>
    ];
  };

  makeBillingAddress = () => {
    const { addresses, payment } = this.props;
    const { testId } = this.context;
    const { addressId: paymentInstrumentAddressId, paymentInstrumentId } = payment;
    const returnTo = paymentInstrumentId ? `/payments/${paymentInstrumentId}/edit` : '/payments/new';

    return (
      <div className={css.billingAddress}>
        <h2>Please Choose a Billing Address</h2>
        <MartyLink
          to={`/addresses/new?returnTo=${encodeURIComponent(returnTo)}`}
          onClick={this.handleAddNewAddressClick}
          className={css.addNewAddress}
          data-test-id={testId('addNewShippingAddress')}>
          Add New Address
        </MartyLink>

        {addresses.length ? (
          <div className={css.savedAddresses} data-test-id={testId('savedAddresses')}>
            {addresses.map(address => {
              const { addressId, name: { fullName }, mailingAddress } = address;
              const { addressLine1, addressLine2, city, stateOrRegion, postalCode, countryName } = mailingAddress;
              const isBillingAddress = paymentInstrumentAddressId === addressId;

              return (
                <div key={addressId}>
                  <div className={css.mobileAddress} data-test-id={testId('mobileSavedAddresses')}>
                    <div className={css.radio}>
                      <input
                        type="radio"
                        id={`mobile-address${addressId}`}
                        name="addressId"
                        defaultChecked={isBillingAddress}
                        defaultValue={addressId}
                        onChange={this.handleAddressChange}
                        data-test-id={testId('radioAddressSelector')}/>
                      <label htmlFor={`mobile-address${addressId}`} aria-label="addressId"/>
                      <label htmlFor={`mobile-address${addressId}`} className={css.addressLabel}>
                        <div data-test-id={testId('shippingAddressCardName')}>{fullName}</div>
                        <div data-test-id={testId('shippingAddressCardAddress1')}>{addressLine1}</div>
                        <div data-test-id={testId('shippingAddressCardAddress2')}>{addressLine2}</div>
                        <div data-test-id={testId('shippingAddressCardCityStateZip')}>{city}, {stateOrRegion} {postalCode}</div>
                        <div data-test-id={testId('shippingAddressCardCountry')}>{countryName}</div>
                      </label>
                    </div>
                  </div>
                  <div className={css.desktopAddress} data-test-id={testId('desktopSavedAddresses')}>
                    <PaymentFormAddressCard
                      address={address}
                      isBillingAddress={isBillingAddress}
                      onAddressChange={this.handleAddressChange}/>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>Please add a billing or shipping address before you add a new payment method.</div>
        )}
      </div>
    );
  };

  render() {
    const { errors, isPrimary } = this.state;
    const { testId } = this.context;
    const {
      payment: {
        addCreditCardNumber,
        expirationMonth,
        expirationYear,
        fullName,
        paymentType,
        primaryPaymentMethod,
        paymentInstrumentId,
        isLoading,
        isFormValid,
        updatePaymentError
      }
    } = this.props;
    const cardType = getTypeInfo(normalizePaymentType(paymentType))?.niceType;

    return (
      <div className={css.paymentForm}>
        <ChangeDisclaimer/>
        {updatePaymentError && <div className={css.errorMsg}>{makeFormErrorMessage(updatePaymentError)}</div>}
        <form method="post" ref="paymentForm" onSubmit={this.handleSubmit}>
          <div className={cn(css.formField, { [css.fieldError]: errors.includes('fullName') })}>
            <label htmlFor="fullName">Name on Card</label>
            <input
              name="fullName"
              id="fullName"
              data-test-id={testId('nameOnCard')}
              placeholder="First and Last"
              maxLength={50}
              defaultValue={fullName}
              onChange={this.handleFullNameChange}
              required/>
          </div>
          <div className={cn(css.formField, { [css.fieldError]: errors.includes('addCreditCardNumber') })}>
            <label htmlFor="addCreditCardNumber">Card Number</label>
            <div className={formStyles.cardNumber}>
              <input
                name="addCreditCardNumber"
                id="addCreditCardNumber"
                data-test-id={testId('addCreditCardNumber')}
                placeholder="Enter card number"
                maxLength={20}
                value={paymentInstrumentId ? `${cardType} ending in ${addCreditCardNumber}` : addCreditCardNumber}
                onChange={this.handleCardNumberChange}
                disabled={Boolean(paymentInstrumentId)}
                required/>
              {this.makeCreditCardIcons()}
            </div>
            <div>{errors.includes('addCreditCardNumber') && PAYMENT_VALIDATION_ERRORS['addCreditCardNumber']}</div>
          </div>
          <fieldset className={cn(css.formField, { [css.fieldError]: errors.includes('expirationDate') })}>
            <legend>Expiration Date</legend>
            <div className={cn(formStyles.expirationDate, { [formStyles.fieldError]: errors.includes('expirationDate') })}>
              <label htmlFor="expirationMonth" className={formStyles.visuallyHidden}>Expiration Month</label>
              <select
                name="expirationMonth"
                id="expirationMonth"
                defaultValue={expirationMonth}
                onChange={this.handleExpirationMonthChange}
                data-test-id={testId('ccExpMonthSelect')}
                required>
                {Array(12).fill().map((_, index) => {
                  const month = (index + 1 + '').padStart(2, '0');
                  return <option key={month} value={month}>{month}</option>;
                })}
              </select>

              <label htmlFor="expirationYear" className={formStyles.visuallyHidden}>Expiration Year</label>
              <select
                name="expirationYear"
                id="expirationYear"
                defaultValue={expirationYear}
                onChange={this.handleExpirationYearChange}
                required
                data-test-id={testId('ccExpYearSelect')}>
                {Array(15).fill().map((_, index) => {
                  const year = moment().year() + index;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
            <div>{errors.includes('expirationDate') && PAYMENT_VALIDATION_ERRORS['expirationDate']}</div>
          </fieldset>
          <div className={css.defaultPayment}>
            {primaryPaymentMethod ? 'This is your primary card' : (
              <div>
                <input
                  type="checkbox"
                  name="primaryPaymentMethod"
                  data-test-id={testId('makePrimaryPayment')}
                  id="primaryPaymentMethod"
                  defaultChecked={isPrimary}
                  onChange={this.handlePrimaryPaymentMethodChange}/>
                <label htmlFor="primaryPaymentMethod" data-test-id={testId('makePrimaryPaymentLabel')}>Make this my primary payment</label>
              </div>
            )}
          </div>
          <hr className={css.divider}/>
          {this.makeBillingAddress()}
          <div className={css.actions}>
            <button
              type="button"
              className={css.cancel}
              onClick={this.handleCancelClick}
              disabled={isLoading}
              data-test-id={testId('cancelButton')}>Cancel</button>
            <button
              type="submit"
              data-test-id={testId('savePayment')}
              className={css.save}
              disabled={!isFormValid && !paymentInstrumentId || isLoading}>
              {isLoading ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    );
  }
}

PaymentForm.contextTypes = {
  router: PropTypes.object.isRequired,
  testId: PropTypes.func
};

PaymentForm.propTypes = {
  payment: PropTypes.object.isRequired,
  addresses: PropTypes.array.isRequired,
  returnTo: PropTypes.string,
  onUpdateFullName: PropTypes.func.isRequired,
  onUpdateCardNumber: PropTypes.func.isRequired,
  onUpdateBillingAddress: PropTypes.func.isRequired,
  onUpdateExpirationMonth: PropTypes.func.isRequired,
  onUpdateExpirationYear: PropTypes.func.isRequired,
  onUpdatePaymentInfo: PropTypes.func.isRequired
};

export default PaymentForm;
